import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Trash2, Plus, Pencil, Copy, Lock, Share2 } from "lucide-react";
import { FounderPriceInline } from "@/components/FounderPriceBlock";

const statusLabel = { published: "Publiée", pending: "En attente", suspended: "Suspendue", rejected: "Refusée" };
const statusColor = { published: "bg-emerald-100 text-emerald-800", pending: "bg-amber-100 text-amber-800",
  suspended: "bg-slate-200 text-slate-700", rejected: "bg-rose-100 text-rose-700" };

export default function ProOffers() {
  const [offers, setOffers] = useState([]);
  const [quota, setQuota] = useState(null);
  const navigate = useNavigate();
  const load = () => api.get("/pro/offers").then((r) => setOffers(r.data));
  useEffect(() => {
    load();
    api.get("/pro/offers/quota").then((r) => setQuota(r.data)).catch(() => {});
  }, []);

  const locked = quota && quota.limit === 0;
  const q = quota || {};
  const daysLeft = q.trial_ends_at ? Math.ceil((new Date(q.trial_ends_at) - new Date()) / 86400000) : null;

  const del = async (id) => {
    if (!confirm("Supprimer cette offre ?")) return;
    await api.delete(`/pro/offers/${id}`); toast.success("Supprimée"); load();
  };
  const duplicate = async (id) => {
    try {
      const { data } = await api.post(`/pro/offers/${id}/duplicate`);
      toast.success("Offre dupliquée");
      navigate(`/pro/offres/${data.id}/editer`);
    } catch { toast.error("Impossible de dupliquer"); }
  };
  const shareOffer = (o) => {
    navigate("/pro/publications-facebook", { state: { prefill: {
      post_type: "offre",
      title: o.title || "",
      offer_text: o.discount ? `${o.title} — ${o.discount}` : (o.title || ""),
      text: `${o.title || ""}${o.discount ? ` — ${o.discount}` : ""}${o.description ? `\n${o.description}` : ""}\nProfitez-en vite, offre à partager autour de vous !`,
    } } });
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-3xl font-bold">Mes offres</h1>
        {locked ? (
          <Link to="/pro/abonnement"><Button className="btn-shine text-white" data-testid="pro-offer-upgrade-btn"><Lock size={15} className="mr-1.5"/> Passer à une formule payante</Button></Link>
        ) : (
          <Link to="/pro/offres/nouvelle"><Button className="btn-shine text-white" data-testid="pro-offer-new-btn"><Plus size={16} className="mr-1"/> Nouvelle offre</Button></Link>
        )}
      </div>

      {q.is_free && q.trial_available && (
        <div className="mt-5 rounded-2xl border border-emerald-300 bg-emerald-50 p-4 flex flex-col md:flex-row items-start md:items-center gap-3" data-testid="pro-trial-available">
          <div className="flex-1">
            <div className="font-heading font-bold text-slate-900">🎁 Activez l'offre Partenaire Fondateur — <FounderPriceInline /></div>
            <div className="text-sm text-slate-700 mt-0.5">Publiez vos offres <b>sans limite</b>, sans carte bancaire et sans engagement. Réservé aux 50 premiers partenaires de Chartres et son agglomération.</div>
          </div>
          <a href="/offre-fondateurs"><Button size="sm" className="btn-shine text-white" data-testid="pro-trial-start-btn"><Plus size={14} className="mr-1"/> Activer — 0 €</Button></a>
        </div>
      )}
      {q.is_free && q.trial_used && daysLeft > 0 && (
        <div className="mt-5 rounded-2xl border border-orange-300 bg-orange-50 p-4 flex flex-col md:flex-row items-start md:items-center gap-3" data-testid="pro-trial-countdown">
          <div className="flex-1">
            <div className="font-heading font-bold text-slate-900">⏳ Votre essai se termine dans {daysLeft} jour{daysLeft > 1 ? "s" : ""}</div>
            <div className="text-sm text-slate-700 mt-0.5">Gardez votre offre en ligne et publiez sans limite avec l'offre <b>Partenaire Fondateur</b> — <FounderPriceInline />, sans carte bancaire.</div>
          </div>
          <a href="/offre-fondateurs"><Button size="sm" className="btn-shine text-white" data-testid="pro-trial-founder-btn">Devenir Fondateur</Button></a>
        </div>
      )}

      {locked && (
        <div className="mt-5 rounded-2xl border border-amber-300 bg-amber-50 p-4 flex flex-col md:flex-row items-start md:items-center gap-3" data-testid="pro-offer-plan-warning">
          <Lock className="text-amber-600 shrink-0" size={22}/>
          <div className="flex-1">
            <div className="font-heading font-bold text-slate-900">{q.is_free && q.trial_used ? "La publication d'offres nécessite l'offre Partenaire Fondateur" : "La publication d'offres nécessite l'offre Partenaire Fondateur"}</div>
            <div className="text-sm text-slate-700 mt-0.5">
              Activez l'offre <b>Partenaire Fondateur</b> (<FounderPriceInline />, sans carte bancaire) pour publier vos offres <b>sans limite</b> et gagner en visibilité locale.
            </div>
          </div>
          <a href="/offre-fondateurs"><Button size="sm" className="btn-shine text-white" data-testid="pro-offer-upgrade-btn-2">{q.is_free && q.trial_used ? "Devenir Fondateur" : "Voir les formules"}</Button></a>
        </div>
      )}
      <div className="mt-6 bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr><th className="p-3 text-left">Titre</th><th className="p-3 text-left">Réduction</th><th className="p-3 text-left">Statut</th><th className="p-3 text-left">Vues</th><th className="p-3 text-left">Clics</th><th className="p-3 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {offers.map((o) => (
              <tr key={o.id} className="border-t border-slate-100">
                <td className="p-3">{o.title}</td>
                <td className="p-3 font-mono">{o.discount}</td>
                <td className="p-3"><Badge className={statusColor[o.status]}>{statusLabel[o.status] || o.status}</Badge></td>
                <td className="p-3">{o.views}</td>
                <td className="p-3">{o.clicks}</td>
                <td className="p-3 text-right">
                  <div className="inline-flex items-center gap-1">
                    {o.status === "published" && (
                      <button onClick={()=>shareOffer(o)}
                              data-testid={`pro-offer-share-${o.id}`}
                              title="Partager sur les réseaux et groupes"
                              className="p-2 rounded-lg text-[#E05638] hover:bg-orange-50">
                        <Share2 size={16}/>
                      </button>
                    )}
                    <button onClick={()=>navigate(`/pro/offres/${o.id}/editer`)}
                            data-testid={`pro-offer-edit-${o.id}`}
                            title="Modifier"
                            className="p-2 rounded-lg text-slate-600 hover:bg-slate-100 hover:text-slate-900">
                      <Pencil size={16}/>
                    </button>
                    <button onClick={()=>duplicate(o.id)}
                            data-testid={`pro-offer-dup-${o.id}`}
                            title="Dupliquer"
                            className="p-2 rounded-lg text-slate-600 hover:bg-slate-100 hover:text-slate-900">
                      <Copy size={16}/>
                    </button>
                    <button onClick={()=>del(o.id)}
                            data-testid={`pro-offer-del-${o.id}`}
                            title="Supprimer"
                            className="p-2 rounded-lg text-rose-600 hover:bg-rose-50">
                      <Trash2 size={16}/>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {offers.length === 0 && <tr><td colSpan="6" className="p-6 text-center text-slate-500">Aucune offre. Créez votre première offre.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
